/**
 * WatchButton — mobile-internal watchlist toggle.
 *
 * Per Tony's mobile architecture rule (banked 2026-05-11): mobile components
 * NEVER import from frontend-react/src/components/WatchList/ (desktop). Mobile
 * reimplements its own UI using the SAME backend endpoints. This component
 * lives entirely inside components/mobile/ and reuses only `watchlistAPI` from
 * the shared services layer.
 *
 * Replaces the no-op `handleWatch` stubs that existed in MobilePlayerCard and
 * MobilePlayerModal pre-9l.2.
 */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { watchlistAPI } from '../../../../services/apiService';
import { dynastyTheme } from '../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

const WatchButton = ({ playerId, style = null, onChange = null }) => {
  const [isWatched, setIsWatched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusKnown, setStatusKnown] = useState(false);

  // Initial status check — populate the icon on mount so user sees their actual state.
  useEffect(() => {
    if (!playerId) return undefined;
    let cancelled = false;
    setStatusKnown(false);
    watchlistAPI.getWatchListStatus(playerId)
      .then((res) => {
        if (cancelled) return;
        setIsWatched(Boolean(res?.is_watched));
        setStatusKnown(true);
      })
      .catch((err) => {
        // 9l.2 NO FALLBACKS: log + remain unwatched. Optimistic toggle still works on click.
        console.error('Watch status check failed:', err);
        if (!cancelled) setStatusKnown(true);
      });
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  const handleToggle = useCallback(async () => {
    if (loading || !playerId) return;
    const previousState = isWatched;
    // Optimistic UI update — flip immediately, rollback on error.
    setIsWatched(!previousState);
    setLoading(true);
    try {
      if (previousState) {
        await watchlistAPI.removeFromWatchList(playerId);
      } else {
        await watchlistAPI.addToWatchList(playerId);
      }
      if (onChange) onChange(!previousState);
    } catch (err) {
      // Rollback optimistic state on failure.
      console.error('Watch toggle failed:', err);
      setIsWatched(previousState);
    } finally {
      setLoading(false);
    }
  }, [playerId, isWatched, loading, onChange]);

  // Visual styling matches QuickActionsBar action button pattern so the Watch
  // button looks consistent with adjacent Add/Drop/Trade buttons.
  const baseStyle = {
    flex: 1,
    minWidth: 0,
    padding: '10px 6px',
    background: isWatched ? C.surface.panelOlive : C.surface[800],
    border: `1px solid ${isWatched ? C.primary : C.surface[900]}`,
    borderRadius: 8,
    color: isWatched ? C.primary : C.coolGray[200],
    fontSize: 10,
    fontWeight: 600,
    cursor: loading ? 'wait' : 'pointer',
    opacity: loading ? 0.6 : 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    transition: 'all 0.15s ease',
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={loading}
      aria-label={isWatched ? 'Remove from watch list' : 'Add to watch list'}
      aria-pressed={isWatched}
      title={isWatched ? 'Remove from watch list' : 'Add to watch list'}
      style={{ ...baseStyle, ...(style || {}) }}
    >
      <span style={{ fontSize: 16 }}>{isWatched ? '★' : '☆'}</span>
      <span>{!statusKnown ? '…' : isWatched ? 'Watching' : 'Watch'}</span>
    </button>
  );
};

WatchButton.propTypes = {
  playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  style: PropTypes.object,
  onChange: PropTypes.func,
};

export default WatchButton;
